// Create an agreement under a partner, or edit its name and notes.
//
// `partner_id` is create-only (see `inputs.ts`): deals carry a composite FK
// proving their agreement belongs to their partner, so re-parenting an
// agreement would drag every deal under it to a different partner. The form
// therefore SHOWS the partner on an existing agreement and refuses to offer it,
// rather than presenting a control whose change the API would reject.

import type { ReactNode } from "react";
import { AgreementCreate, AgreementUpdate, CostBillingMode } from "@crm/shared";
import type { Agreement } from "@crm/shared";
import { useAgreementMutations } from "../../api/mutations.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { ReadOnlyField, SelectField, TextField } from "../../forms/fields.js";
import type { SelectOption } from "../../forms/fields.js";
import { optionalText } from "../../forms/parseInput.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import { costBillingModeLabel } from "../../format/labels.js";

interface AgreementDraft {
  partner_id: string;
  name: string;
  notes: string;
  cost_billing_mode: string;
}

const LABELS = {
  partner_id: "Partner",
  name: "Agreement name",
  notes: "Notes",
  cost_billing_mode: "Cost recharges",
} as const;

const BILLING_MODE_OPTIONS: readonly SelectOption<CostBillingMode>[] = CostBillingMode.options.map(
  (mode) => ({ value: mode, label: costBillingModeLabel(mode) }),
);

function draftFromRecord(agreement: Agreement): AgreementDraft {
  return {
    partner_id: agreement.partner_id,
    name: agreement.name,
    notes: agreement.notes ?? "",
    cost_billing_mode: agreement.cost_billing_mode,
  };
}

function toWire(draft: AgreementDraft): Wire {
  return {
    partner_id: draft.partner_id,
    name: draft.name.trim(),
    notes: optionalText(draft.notes),
    cost_billing_mode: draft.cost_billing_mode,
  };
}

export interface AgreementFormDialogProps {
  partnerId: string;
  /** For the read-only partner row — an id tells a reader nothing. */
  partnerName: string;
  /** Absent for a new agreement. */
  agreement?: Agreement;
  onClose: () => void;
  onSaved?: (agreement: Agreement) => void;
}

export function AgreementFormDialog({
  partnerId,
  partnerName,
  agreement,
  onClose,
  onSaved,
}: AgreementFormDialogProps): ReactNode {
  const mutations = useAgreementMutations();
  const isNew = agreement === undefined;

  const editor = useEditor<AgreementDraft, AgreementCreate, AgreementUpdate, Agreement>({
    mode: agreement === undefined ? { kind: "create" } : { kind: "update", record: agreement },
    blankDraft: { partner_id: partnerId, name: "", notes: "", cost_billing_mode: "bundled" },
    draftFromRecord,
    toWire,
    createSchema: AgreementCreate,
    updateSchema: AgreementUpdate,
    create: (body, idempotencyKey) => mutations.create.mutateAsync({ body, idempotencyKey }),
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: (saved) => {
      onSaved?.(saved);
      onClose();
    },
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title={isNew ? "New agreement" : "Edit agreement"}
      description={
        isNew
          ? `A new agreement under ${partnerName}. Its split percentages are effective-dated terms, added separately — an agreement with no term cannot resolve a split.`
          : undefined
      }
      submitLabel={isNew ? "Create agreement" : "Save changes"}
      editor={editor}
      labels={LABELS}
    >
      <ReadOnlyField
        label={LABELS.partner_id}
        value={partnerName}
        reason="An agreement belongs to the partner it was signed with. Deals prove that pairing with a composite key, so moving the agreement would move every deal under it — do that deal by deal instead."
      />
      <TextField
        label={LABELS.name}
        required
        value={editor.draft.name}
        error={editor.errors["name"]}
        onChange={(value) => editor.setField("name", value)}
      />
      <TextField
        label={LABELS.notes}
        multiline
        value={editor.draft.notes}
        error={editor.errors["notes"]}
        onChange={(value) => editor.setField("notes", value)}
      />
      {/* Advisory, and the hint says so: bundling IS giving the recharge the
          same invoice number as that month's charges, which nothing here
          can make an external accounting system do. What this records is the
          layout that was agreed, so it is looked up rather than remembered. */}
      <SelectField
        label={LABELS.cost_billing_mode}
        required
        value={editor.draft.cost_billing_mode}
        options={BILLING_MODE_OPTIONS}
        error={editor.errors["cost_billing_mode"]}
        hint="How this agreement's shared costs are meant to reach the partner. Bundled means the recharge goes out on the same invoice as that period's subscription lines."
        onChange={(value) => editor.setField("cost_billing_mode", value)}
      />
    </FormDialog>
  );
}
